import { inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { type WritableDraft } from 'immer';
import { combineLatest, first, map, type Observable, of, switchMap } from 'rxjs';

import { runtimeSelectors } from '@blaze-runtime/engine-data-access';
import {
  type CanvasElementFrom,
  type CanvasElementId,
  type PartialPropsOf,
} from '@blaze-runtime/engine-domain';
import { applyPropsPatch } from '@blaze-runtime/engine-domain';
import { ComparisonOperator, LogicalType, QueryNodeType } from '@blaze-shared/api-contracts-domain';
import { TablesCollectionService } from '@blaze-shared/datasets-data-access';
import { SchemaService } from '@blaze-shared/schema-data-access';
import { type BlazeTableSchema } from '@blaze-shared/schema-domain';
import { isGetNode, type Workflow } from '@blaze-shared/workflow-domain';

import { RuntimeFacade } from '../../../+state';
import { ElementsFacade } from '../../../+state/elements';
import { type EngineState } from '../../../+state/engine';
import {
  addWorkflowWithConnection,
  bindValueToWorkflowSource,
  createNewWorkflowRequest,
  dispatchValueBindingRefresh,
  type ElementHandler,
  findElement,
  markWorkflowForceUpdateIfWired,
  upsertElement,
  wireLayoutWorkflowInputs,
} from '../../../ai';
import { AiWorkflowGenerationService, ComponentSourceWorkflowService } from '../../../ai/workflow';
import { ElementDefinitionsService } from '../../../services/element-definitions.service';
import { WorkflowCrudService } from '../../../services/workflow-crud.service';

import { CalendarActionsHandler } from './calendar-actions.handler';
import { type CalendarContext, type CalendarResolvedContext } from './calendar.definition';

export const CALENDAR_REFRESH_TIMEOUT_MS = 10_000;

export class CalendarElementHandler implements ElementHandler<'calendar'> {
  private readonly store = inject(Store);
  private readonly workflowCrudService = inject(WorkflowCrudService);
  private readonly calendarActionsHandler = inject(CalendarActionsHandler);
  private readonly elementsFacade = inject(ElementsFacade);
  private readonly runtimeFacade = inject(RuntimeFacade);
  private readonly schemaService = inject(SchemaService);
  private readonly tablesCollectionService = inject(TablesCollectionService);
  private readonly componentSourceWorkflowService = inject(ComponentSourceWorkflowService);
  private readonly elementDefinitions = inject(ElementDefinitionsService);
  private readonly aiWorkflowGenerationService = inject(AiWorkflowGenerationService);

  readonly type = 'calendar';

  prepare(context: CalendarContext): Observable<CalendarResolvedContext> {
    const entry = context.componentWorkflows[context.element.id];

    if (entry) {
      return this.componentSourceWorkflowService
        .ensureCreated(entry, context.projectId, context.pageId)
        .pipe(map((workflowContext) => ({ ...context, workflowContext })));
    }

    let workflowContext$: Observable<Workflow | undefined> = of(undefined);

    const { dataSourceId, calendarColumn } = context.element;

    if (dataSourceId && context.element.filter === 'CurrentUser' && calendarColumn) {
      workflowContext$ = this.createElementWorkflowWithDataFilter(
        dataSourceId,
        context.pageId,
        context.projectId,
        calendarColumn,
      );
    } else if (dataSourceId) {
      workflowContext$ = this.createElementWorkflow(
        dataSourceId,
        context.pageId,
        context.projectId,
      );
    } else {
      console.warn(
        'Calendar element has no valid data source for events. Workflow will not be generated.',
      );
    }

    return this.store.select(runtimeSelectors.selectProjectId).pipe(
      first(Boolean),
      switchMap((projectId) =>
        this.tablesCollectionService.getWithQuery({ projectId }).pipe(first()),
      ),
      switchMap(() =>
        workflowContext$.pipe(
          map((workflow) =>
            workflow ? { ...context, workflowContext: { workflow, inputVariables: [] } } : context,
          ),
        ),
      ),
    );
  }

  process(context: CalendarResolvedContext, draft: WritableDraft<EngineState>): void {
    const elementProps = this.getCalendarProps(context, draft);
    upsertElement(draft, context, elementProps);

    const workflow = context.workflowContext?.workflow;
    if (!context.element.dataSourceId && workflow?.id) {
      bindValueToWorkflowSource(draft, context, workflow);
      context.workflowsToFinalize = [{ workflow }];
    }
  }

  wireCrossComponentInputs(
    context: CalendarResolvedContext,
    draft: WritableDraft<EngineState>,
  ): void {
    const workflow = context.workflowContext?.workflow;
    if (context.element.dataSourceId || !workflow?.id) {
      return;
    }

    const hasUpdates = wireLayoutWorkflowInputs(draft, context, workflow, this.elementDefinitions);
    markWorkflowForceUpdateIfWired(context.workflowsToFinalize, workflow, hasUpdates);
  }

  finalize(
    element: CanvasElementFrom<'calendar'>,
    context: CalendarResolvedContext,
  ): Observable<void> {
    const persist$ = this.aiWorkflowGenerationService
      .handleAiGeneratedWorkflows(context.workflowsToFinalize ?? [])
      .pipe(switchMap(() => this.refreshBinding(element.id)));

    if (!context.componentWorkflows) {
      return persist$;
    }

    return persist$.pipe(
      switchMap(() =>
        combineLatest([
          this.calendarActionsHandler.finalizeRowActionDialogs(element, context),
          this.calendarActionsHandler.finalizeRowActionWorkflows(element, context),
        ]),
      ),
      map((): void => undefined),
    );
  }

  private getInitialProps(
    context: CalendarResolvedContext,
    draft: WritableDraft<EngineState>,
  ): PartialPropsOf<'calendar'> {
    const element = findElement<'calendar'>(draft, context.element.id);
    return element?.props ?? {};
  }

  private getCalendarProps(
    context: CalendarResolvedContext,
    draft: WritableDraft<EngineState>,
  ): PartialPropsOf<'calendar'> {
    let props = this.getInitialProps(context, draft);
    const workflow = context.workflowContext?.workflow;
    const {
      titleColumn,
      startColumn,
      endColumn,
      calendarColumn,
      timeZoneColumn,
      dataSourceId,
      dragAndDrop,
    } = context.element;

    if (dataSourceId && workflow?.id) {
      const schema = draft.schemas[dataSourceId];
      if (!schema) {
        throw new Error('Schema for events data source not found');
      }
      props.data = [
        ...(props.data || []).filter((data) => data.key !== 'value'),
        {
          key: 'value',
          enabled: true,
          bindings: {},
          isCustomSelection: false,
          dataSource: {
            id: workflow.id,
            name: schema?.name,
            sourceType: 'context',
            columns: schema.columns,
          },
        },
      ];
      addWorkflowWithConnection(draft, context, workflow);
    }

    props = applyPropsPatch(props, {
      content: {
        input: {
          ...(titleColumn && { titleColumn: titleColumn }),
          ...(startColumn && { startColumn: startColumn }),
          ...(endColumn && { endColumn: endColumn }),
          ...(calendarColumn && { calendarColumn: calendarColumn }),
          ...(timeZoneColumn && { timeZoneColumn: timeZoneColumn }),
        },
      },
    });

    if (dragAndDrop) {
      props = applyPropsPatch(props, {
        content: {
          calendarOptions: {
            disableEventDrag: !dragAndDrop,
          },
        },
      });
    }

    const actions = context.element.actions;
    if (actions?.length) {
      if (actions.includes('Create')) {
        props = applyPropsPatch(props, {
          content: {
            itemAdd: { enabled: true, label: '' },
          },
        });
      }
      if (actions.includes('Edit')) {
        props = applyPropsPatch(props, {
          content: {
            itemEdit: { enabled: true },
          },
        });
      }
      if (actions.includes('View')) {
        props = applyPropsPatch(props, {
          content: {
            itemViewDialog: { enabled: true },
          },
        });
      }
      if (actions.includes('Delete')) {
        props = applyPropsPatch(props, {
          content: {
            itemDelete: { enabled: true },
          },
        });
      }
    }

    return props;
  }

  private createElementWorkflowWithDataFilter(
    dataSourceId: string,
    pageId: string,
    projectId: string,
    filterColumn: string,
  ): Observable<Workflow> {
    return this.schemaService.obtainUsersSchema().pipe(
      first(Boolean),
      switchMap((schema: BlazeTableSchema) => {
        const userIdColumn = schema.columns.find((column) => column.isId)?.id;
        const newWorkflowRequest = createNewWorkflowRequest(dataSourceId, pageId, projectId);
        const getNode = newWorkflowRequest.nodes.find(isGetNode);
        if (getNode) {
          getNode.query = {
            children: [
              {
                children: [],
                column: filterColumn,
                displayInfos: [],
                ignoreIfEmptyValue: false,
                isFormulaValue: false,
                isOptional: false,
                nodeType: QueryNodeType.Comparison,
                operator: ComparisonOperator.Eq,
                value: `$$User["${userIdColumn}"]`,
              },
            ],
            logicalType: LogicalType.And,
            nodeType: QueryNodeType.Logical,
          };
        }
        return this.workflowCrudService.createWorkflow(newWorkflowRequest);
      }),
    );
  }

  private createElementWorkflow(
    dataSourceId: string,
    pageId: string,
    projectId: string,
  ): Observable<Workflow> {
    return this.workflowCrudService.createWorkflow(
      createNewWorkflowRequest(dataSourceId, pageId, projectId),
    );
  }

  private refreshBinding(elementId: CanvasElementId): Observable<void> {
    return dispatchValueBindingRefresh({
      elementsFacade: this.elementsFacade,
      runtimeFacade: this.runtimeFacade,
      store: this.store,
      elementId,
      timeoutMs: CALENDAR_REFRESH_TIMEOUT_MS,
      onTimeout: () => console.warn(`Calendar ${elementId}: value binding schema did not resolve`),
      onError: (error) => console.warn(`Calendar ${elementId}: refresh binding failed`, error),
    });
  }
}
